import React, { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import {
    RadioSongCard,
    RadioSongThreadSurface,
    useOpenRadioSongThread,
} from 'components/answers/PostAnswer'
import PanelLoadingIndicator from './PanelLoadingIndicator'
import {
    RadioArtistProfile,
    artistUrl,
    formatPlays,
    formatSongs,
    sortSongs,
} from './radioArtistsModel'
import { ARTISTS_GC_MS, ARTISTS_STALE_MS, getJson } from './radioArtistsParts'
import { useMusicPanelView } from './musicPanelView'
import styles from './radio-artists.module.scss'

const SONG_CARD_TEST_IDS = {
    artist_profile: 'artist-song-card',
    music_discover: 'discover-song-card',
    my_music: 'my-music-song-card',
}

// The song as its radio card and nothing around it: the card's own player
// carries the playlist and upvalue buttons, its media menu Share. A click on
// the card opens the song's thread (the player's own controls keep their clicks).
export function RadioSongItem({
    answerId,
    title,
    source,
}: {
    answerId: string
    title?: string
    source: 'artist_profile' | 'music_discover' | 'my_music'
}) {
    // Batches with the other rows on screen into one bulk read, like the regular
    // feed; the card reads the canonical answer once it's cached.
    useCachedAnswer(answerId)
    useEffect(() => {
        queueAnswerHydration(answerId)
    }, [answerId])
    const openSongThread = useOpenRadioSongThread(null, answerId, source)

    return (
        <div className={styles.song}>
            <RadioSongThreadSurface
                openSongThread={openSongThread}
                testId={SONG_CARD_TEST_IDS[source]}
                bodyClassName={styles.songCard}
            >
                <RadioSongCard
                    answer={null}
                    songId={answerId}
                    title={title}
                    showByline={false}
                    upvalueInPlayer={`radio_${source}`}
                />
            </RadioSongThreadSurface>
        </div>
    )
}

// The profile's Music tab (filter.user_music): the user's radio earnings and
// songs. The profile header above it carries the songs order. A user who never
// aired gets the empty state (the artist endpoint 404s for them).
export default function MusicArtistProfile({
    artistId,
    panelId,
}: {
    artistId: string
    panelId?: string
}) {
    const { songOrder } = useMusicPanelView(panelId)
    const query = useQuery<{ artist: RadioArtistProfile | null }>({
        // The server orders before its 50-song limit, so each order is its own list.
        queryKey: ['radioArtist', artistId, songOrder],
        staleTime: ARTISTS_STALE_MS,
        gcTime: ARTISTS_GC_MS,
        refetchOnMount: true,
        queryFn: () =>
            getJson(artistUrl(artistId, songOrder)).catch(error =>
                String(error?.message).includes('404') ? { artist: null } : Promise.reject(error),
            ),
    })
    const artist = query.data?.artist
    const tracks = artist ? sortSongs(artist.tracks, songOrder) : []

    return (
        <div className={styles.profile}>
            {artist && (
                <div className={styles.musicStats} data-testid="profile-music-stats">
                    {formatPlays(artist.play_count)} on Treechat Radio ·{' '}
                    {formatSongs(artist.track_count)}
                </div>
            )}
            {query.isLoading && <PanelLoadingIndicator label="Loading music" />}
            {query.isError && <div className={styles.empty}>Couldn't load this music.</div>}
            {query.isSuccess && tracks.length === 0 && (
                <div className={styles.empty}>No songs on the radio yet.</div>
            )}
            {tracks.map(track => (
                <RadioSongItem
                    key={track.answer_id}
                    answerId={track.answer_id}
                    title={track.title}
                    source="artist_profile"
                />
            ))}
        </div>
    )
}
